import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import cx from 'classnames';

import withStyles from '@mui/styles/withStyles';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';

import LaunchIcon from '@mui/icons-material/Launch';
import CloseIcon from '@mui/icons-material/Close';
import MarkActiveIcon from '@mui/icons-material/Unarchive';
import MarkInactiveIcon from '@mui/icons-material/Archive';
// import MarkActiveIcon from '@mui/icons-material/FlightTakeoff';
// import MarkInactiveIcon from '@mui/icons-material/FlightLand';

import { removeContact } from '../store/slices/emergencyContactsSlice';
import { updatePerson } from '../store/slices/personsSlice';
import {
  setIntendedReferrerCompanyId,
  setIntendedReferrerContactId,
  setIntendedEmergencyContactId,
  setIntendedEmergencyContacteeId,
} from '../store/slices/intendedInfoSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import { createStdRequest, apiFetch } from '../utils/utils';

import contactAssocGroupStyles from '../assets/jss/crm/components/contactAssocGroupStyles';

import DA from '../store/ReduxAccess';
import ReduxLoader from '../store/ReduxLoader';

import ConfirmModal from './ConfirmModal';

function toggleActivity(page, contact, viewingFirmId, visibleFirmId, updatePerson, refreshFunc) {
  const newInactive = !contact.inactive;
  const revisedContact = { ...contact, inactive: newInactive };
  let request = null;
  const forEmergencyContact = !visibleFirmId;
  if (forEmergencyContact) {
    const clientId = page.props.clientId;
    const contactId = contact.personId;
    const uri = `/api/person/${clientId}/contact/${contactId}`;
    const apiStruct = buildApiStructForModifyingContactualInfo(clientId, contactId, viewingFirmId, newInactive);
    request = createStdRequest(uri, 'put', apiStruct);
  } else {
    /* for Firm Contact */
    const uri = `/api/firm/${viewingFirmId}/visibleFirm/${visibleFirmId}/contact`;
    request = createStdRequest(uri, 'post', revisedContact);
  }
  const actionOnGoodResponse = (json) => {
    /* store revised state in Redux */
    const reduxRep = DA.getPersonById(contact.personId);
    const revisedRep = { ...reduxRep, inactive: revisedContact.inactive };
    updatePerson(revisedRep);
    refreshFunc('showActiveContacts');
  };
  apiFetch(page, request, actionOnGoodResponse);
}

function buildApiStructForModifyingContactualInfo(clientId, contactId, viewingFirmId, inactive) {
  return {
    clientId,
    contactId,
    relationship: DA.getClientContactRelationship(clientId, contactId),
    description: DA.getClientContactDescription(clientId, contactId),
    viewingFirmId,
    inactive,
  };
}

const ContactActivationWidget = ({
  classes,
  page,
  contact,
  viewingFirmId,
  visibleFirmId,
  updatePerson,
  refreshFunc,
}) => {
  const tooltipText = contact.inactive ? 'Mark Active' : 'Mark Inactive';
  return (
    <div className={classes.entitySummary_icon}>
      <div className={cx(classes.profileImageComponent, classes.profileImageComponent_circle)}>
        <div
          className={classes.profileImageComponent_initials}
          onClick={() => toggleActivity(page, contact, viewingFirmId, visibleFirmId, updatePerson, refreshFunc)}
        >
          <Tooltip title={tooltipText} placement="top">
            <span className={classes.u_cursorPointer}>
              {contact.inactive ? <MarkActiveIcon /> : <MarkInactiveIcon />}
            </span>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

class ContactSummary extends React.Component {
  state = {
    redirect: undefined,
    redirectAndPush: undefined,
    shouldShowDeletionConfirmationModal: false,
  };

  generateEmailLinkForContact(contact) {
    return `mailto:${contact.email}?bcc=abcdefg@allevasoft.com`;
  }

  launchFullOrgContactPage(contact, referrerOrg) {
    if (referrerOrg) {
      this.props.setIntendedReferrerCompanyId(referrerOrg.firmId);
    }
    this.props.setIntendedReferrerContactId(contact.personId);
    this.setState({ redirectAndPush: 'referrerContact' });
  }

  launchFullEmergencyContactPage(contact) {
    this.props.setIntendedEmergencyContactId(contact.personId);
    this.props.setIntendedEmergencyContacteeId(this.props.clientId);
    this.setState({ redirectAndPush: 'emergencyContact' });
  }

  offerToRemoveContact(contact) {
    const { referrerOrg, clientId } = this.props;
    if (referrerOrg) {
      this.confirmAndMaybeRemoveOrgContact(referrerOrg, contact);
    } else {
      this.confirmAndMaybeRemoveClientContact(clientId, contact);
    }
  }

  confirmAndMaybeRemoveOrgContact(referrerOrg, contact) {
    const q = `Are you sure you want to remove ${contact.preferredFullName} as contact at this company?`;
    this.setState({
      shouldShowDeletionConfirmationModal: true,
      deletionConfirmationQuery: q,
      deleteContactFunc: () => this.removeOrgContact(referrerOrg, contact),
    });
  }

  removeOrgContact(referrerOrg, contact) {
    const viewingFirmId = DA.getFirmId();
    const contactId = contact.personId;
    const visibleFirmId = referrerOrg.firmId;
    const uri = `/api/firm/${viewingFirmId}/visibleFirm/${visibleFirmId}/contact/${contactId}`;
    const request = createStdRequest(uri, 'DELETE');
    const actionOnGoodResponse = () => {
      // remove that contact from Redux store
      ReduxLoader.removeVisibleFirmContact(visibleFirmId, contactId);
      // do something to cause repainting
      this.props.refreshFunc();
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  confirmAndMaybeRemoveClientContact(clientId, contact) {
    const q = `Are you sure you want to remove ${contact.preferredFullName} as contact of this client?`;
    this.setState({
      shouldShowDeletionConfirmationModal: true,
      deletionConfirmationQuery: q,
      deleteContactFunc: () => this.removeClientContact(clientId, contact),
    });
  }

  removeClientContact(clientId, contact) {
    const viewingFirmId = DA.getFirmId();
    const contactId = contact.personId;
    const uri = `/api/person/${clientId}/viewingFirm/${viewingFirmId}/contact/${contactId}`;
    const request = createStdRequest(uri, 'DELETE');
    const actionOnGoodResponse = () => {
      // remove that contact from Redux store
      this.props.removeContact({ personId: clientId, contactId });
      // do something to cause repainting
      this.props.refreshFunc();
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  render() {
    if (this.state.redirectAndPush) {
      return <Redirect to={this.state.redirectAndPush} push />;
    }

    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} />;
    }

    const { classes, contact, referrerOrg } = this.props;
    if (referrerOrg) {
      return this.renderAsOrgContact(classes, contact, referrerOrg);
    } else {
      return this.renderAsEmergencyContact(classes, contact);
    }
  }

  renderAsOrgContact(classes, contact, referrerOrg) {
    const phoneNumbers = contact.phoneNumbers;
    const firstPhoneNumber = phoneNumbers && phoneNumbers.length > 0 ? phoneNumbers[0] : null;
    const phoneNumber = firstPhoneNumber && firstPhoneNumber.number;

    return (
      <div className={cx(classes.entitySummary, classes.entitySummary_contact)}>
        <ConfirmModal
          show={this.state.shouldShowDeletionConfirmationModal}
          title="Confirm Deletion"
          confirmButtonLabel="Delete"
          rejectButtonLabel="Cancel"
          onConfirm={this.state.deleteContactFunc}
          onReject={() => this.setState({ shouldShowDeletionConfirmationModal: false })}
        >
          <div>{this.state.deletionConfirmationQuery}</div>
        </ConfirmModal>
        <ContactActivationWidget
          classes={classes}
          page={this}
          contact={contact}
          viewingFirmId={DA.getFirmId()}
          visibleFirmId={referrerOrg.firmId}
          updatePerson={this.props.updatePerson}
          refreshFunc={this.props.refreshFunc}
        />
        <div className={classes.entitySummary_buttonContainer}>
          <Tooltip title="Launch Full Page" placement="top">
            <IconButton onClick={() => this.launchFullOrgContactPage(contact, referrerOrg)} size="large">
              <LaunchIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Disassociate" placement="top">
            <IconButton onClick={() => this.offerToRemoveContact(contact)} size="large">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </div>
        <div className={classes.u_displayFlex}>
          <div className={cx(classes.entitySummary_title, classes.ac_h4)}>
            <span
              className={cx(classes.u_cursorPointer, classes.u_ellipsis)}
              onClick={() => this.launchFullOrgContactPage(contact, referrerOrg)}
            >
              {contact.preferredFullName}
            </span>
            {contact.inactive && ' (inactive)'}
          </div>
        </div>
        <div className={cx(classes.entitySummary_subtitle, classes.u_ellipsis, classes.ac_h5)}>
          <span className={classes.entitySummary_subtitleName}>{contact.jobTitle}</span>
          {' at '}
          <span className={classes.entitySummary_subtitleName}>{referrerOrg.name}</span>
          <div className={classes.entitySummary_subtitleGroup}>
            {phoneNumber && (
              <span>
                <div className={classes.u_clickThrough}>
                  <div className={classes.u_ellipsis}>{phoneNumber}</div>
                </div>
              </span>
            )}
            {contact.email && (
              <span>
                <Link className={classes.ac_a} href={this.generateEmailLinkForContact(contact)} underline="hover">
                  {contact.email}
                </Link>
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderAsEmergencyContact(classes, contact) {
    return (
      <div className={cx(classes.entitySummary, classes.entitySummary_contact)}>
        <ContactActivationWidget
          classes={classes}
          page={this}
          contact={contact}
          viewingFirmId={DA.getFirmId()}
          updatePerson={this.props.updatePerson}
          refreshFunc={this.props.refreshFunc}
        />
        <div className={classes.entitySummary_buttonContainer}>
          <Tooltip title="Disassociate" placement="top">
            <IconButton onClick={() => this.offerToRemoveContact(contact)} size="large">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </div>
        <div className={classes.u_displayFlex}>
          <div className={cx(classes.entitySummary_title, classes.ac_h4)}>
            <span
              className={cx(classes.u_cursorPointer, classes.u_ellipsis)}
              onClick={() => this.launchFullEmergencyContactPage(contact)}
            >
              {contact.preferredFullName}
            </span>
          </div>
        </div>
        <div className={cx(classes.entitySummary_subtitle, classes.u_ellipsis, classes.ac_h5)}>
          <span className={classes.entitySummary_subtitleName}>Relationship:&nbsp;</span>
          <span className={classes.entitySummary_subtitleName}>{contact.relationship}</span>
          <div className={classes.entitySummary_subtitleGroup}>
            <span>
              <div className={classes.u_clickThrough}>
                <div className={classes.u_ellipsis}>{contact.phone}</div>
              </div>
            </span>
            {contact.email && (
              <span>
                <Link className={classes.ac_a} href={this.generateEmailLinkForContact(contact)} underline="hover">
                  {contact.email}
                </Link>
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({});

const actionProducers = {
  setIntendedReferrerCompanyId,
  setIntendedReferrerContactId,
  setIntendedEmergencyContactId,
  setIntendedEmergencyContacteeId,
  removeContact,
  updatePerson,
  setApiResponseInfo,
};

export default connect(mapStateToProps, actionProducers)(withStyles(contactAssocGroupStyles)(ContactSummary));
